import type { CSSProperties } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTheme } from '../../theme';

export interface AdminCoursePerformanceRow {
  name: string;
  completion: number;
  inProgress: number;
}

export interface AdminCoursePerformanceChartProps {
  rows: AdminCoursePerformanceRow[];
  legendCompletionLabel: string;
  legendInProgressLabel: string;
  loadingLabel: string;
  emptyLabel: string;
  errorLabel: string;
  loading?: boolean;
  error?: boolean;
}

export function AdminCoursePerformanceChart({
  rows,
  legendCompletionLabel,
  legendInProgressLabel,
  loadingLabel,
  emptyLabel,
  errorLabel,
  loading,
  error,
}: AdminCoursePerformanceChartProps) {
  const theme = useTheme();
  const c = theme.palette.adminDashboard.charts;
  const h = c.chartHeightPx;

  const bodyStyle: CSSProperties = {
    width: '100%',
    height: h,
    minHeight: h,
  };

  if (loading) {
    return (
      <p
        style={{
          margin: 0,
          color: c.subtitleColor,
          fontSize: theme.typography.fontSize.base,
        }}
      >
        {loadingLabel}
      </p>
    );
  }

  if (error) {
    return (
      <p
        style={{
          margin: 0,
          color: theme.palette.error.main,
          fontSize: theme.typography.fontSize.base,
        }}
      >
        {errorLabel}
      </p>
    );
  }

  if (!rows.length) {
    return (
      <p
        style={{
          margin: 0,
          color: c.subtitleColor,
          fontSize: theme.typography.fontSize.base,
        }}
      >
        {emptyLabel}
      </p>
    );
  }

  return (
    <div style={bodyStyle}>
      <ResponsiveContainer
        width="100%"
        height="100%"
      >
        <BarChart
          data={rows}
          margin={{ top: 8, right: 8, left: 0, bottom: 4 }}
          barGap={0}
          barCategoryGap="28%"
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={c.gridStroke}
            vertical={false}
          />
          <XAxis
            dataKey="name"
            tick={{ fill: c.axisTick, fontSize: 12 }}
            axisLine={{ stroke: c.gridStroke }}
            tickLine={false}
          />
          <YAxis
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            tickFormatter={v => `${v}`}
            tick={{ fill: c.axisTick, fontSize: 12 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            formatter={(value: number) => `${value}%`}
            contentStyle={{
              borderRadius: theme.shape.borderRadius.sm2,
              border: `${theme.shape.borderWidth.thin} solid ${c.cardBorder}`,
            }}
          />
          <Legend
            wrapperStyle={{ fontSize: theme.typography.fontSize.xs }}
          />
          <Bar
            dataKey="completion"
            name={legendCompletionLabel}
            fill={c.barCompletion}
            radius={[6, 6, 0, 0]}
            maxBarSize={40}
          />
          <Bar
            dataKey="inProgress"
            name={legendInProgressLabel}
            fill={c.barInProgress}
            radius={[6, 6, 0, 0]}
            maxBarSize={40}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
